import { AlertCircle, CheckCircle2, Clock3, HelpCircle, Info, Loader2, RefreshCw, ShieldCheck, ShoppingBag } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LinkButton } from "../components/Button";
import { EmptyState } from "../components/UI";
import { verifyPayment } from "../lib/api";

const PENDING_PAYMENT_KEY = "pd_pending_payment";

type PendingPayment = {
  order_id?: string | number;
  display_id?: string;
  gateway_order_id?: string;
  provider?: string;
};

type PaymentReturnState = "checking" | "success" | "failed" | "expired";

const content = {
  about: {
    icon: Info,
    title: "About Us",
    body: "ProtoDeccan provides custom FDM 3D printing from Bengaluru with a practical upload, quote, and checkout workflow."
  },
  faq: {
    icon: HelpCircle,
    title: "FAQ",
    body: "The React FAQ route is ready. The next slice can connect it to the existing FAQ categories and accordion APIs."
  },
  privacy: {
    icon: ShieldCheck,
    title: "Privacy Policy",
    body: "Legal content will be loaded from the existing backend legal endpoint during the content migration slice."
  },
  terms: {
    icon: ShieldCheck,
    title: "Terms & Conditions",
    body: "Terms content will be wired to the current legal page APIs."
  },
  refund: {
    icon: ShieldCheck,
    title: "Refund Policy",
    body: "Refund policy content will be wired to the current legal page APIs."
  },
  payment: {
    icon: ShieldCheck,
    title: "Payment Return",
    body: "Payment verification UI is ready for the existing payment return flow."
  }
};

export function InfoPage({ type }: { type: keyof typeof content }) {
  if (type === "payment") return <PaymentReturnPage />;

  const item = content[type];
  const Icon = item.icon;

  return (
    <EmptyState icon={<Icon size={24} />} title={item.title} action={<LinkButton to="/quote">Get A Quote</LinkButton>} className="info-page">
      {item.body}
    </EmptyState>
  );
}

function readPendingPayment(): PendingPayment {
  try {
    return JSON.parse(localStorage.getItem(PENDING_PAYMENT_KEY) || "{}") as PendingPayment;
  } catch {
    return {};
  }
}

function firstParam(params: URLSearchParams, keys: string[]) {
  for (const key of keys) {
    const value = params.get(key);
    if (value) return value;
  }
  return "";
}

function classifyPaymentFailure(message: string): Exclude<PaymentReturnState, "checking" | "success"> {
  const normalized = message.toLowerCase();
  if (normalized.includes("expired") || normalized.includes("expire") || normalized.includes("timeout")) {
    return "expired";
  }
  return "failed";
}

const paymentReturnContent: Record<PaymentReturnState, { title: string; body: string }> = {
  checking: {
    title: "Confirming payment",
    body: "Confirming your payment with PhonePe."
  },
  success: {
    title: "Order placed successfully",
    body: "Your payment is confirmed and your order is queued."
  },
  failed: {
    title: "Payment was not completed",
    body: "We could not confirm this payment. No paid order has been queued yet."
  },
  expired: {
    title: "Payment session expired",
    body: "This payment session expired before completion. Please start checkout again when you are ready."
  }
};

function PaymentReturnPage() {
  const [params] = useSearchParams();
  const pending = useMemo(readPendingPayment, []);
  const gatewayOrderId = firstParam(params, ["gateway_order_id", "merchantOrderId", "merchantTransactionId"]) || String(pending.gateway_order_id || "");
  const orderId = firstParam(params, ["order_id", "orderId"]) || String(pending.order_id || pending.display_id || "");
  const [state, setState] = useState<PaymentReturnState>(gatewayOrderId ? "checking" : "failed");
  const [message, setMessage] = useState(gatewayOrderId ? paymentReturnContent.checking.body : "We could not find a payment reference in this return link.");

  useEffect(() => {
    if (!gatewayOrderId) return;
    let cancelled = false;
    verifyPayment(gatewayOrderId)
      .then(() => {
        if (cancelled) return;
        localStorage.removeItem(PENDING_PAYMENT_KEY);
        setState("success");
        setMessage("Your order has been placed successfully.");
      })
      .catch((error) => {
        if (cancelled) return;
        const nextMessage = error instanceof Error ? error.message : "Payment verification failed. Please check your order status.";
        setState(classifyPaymentFailure(nextMessage));
        setMessage(nextMessage);
      });
    return () => {
      cancelled = true;
    };
  }, [gatewayOrderId]);

  const Icon = state === "checking" ? Loader2 : state === "success" ? CheckCircle2 : state === "expired" ? Clock3 : AlertCircle;
  const orderPath = orderId ? `/orders/${encodeURIComponent(orderId)}` : "/orders";
  const repayPath = orderId ? `/orders/${encodeURIComponent(orderId)}/pay` : "/orders";
  const content = paymentReturnContent[state];
  const body = state === "success" || state === "checking" ? content.body : message || content.body;

  return (
    <EmptyState
      icon={<Icon className={state === "checking" ? "spin" : undefined} size={26} />}
      title={content.title}
      action={(
        <div className="payment-return-actions">
          {state === "success" ? <LinkButton to={orderPath}>Check order status</LinkButton> : null}
          {state !== "success" ? <LinkButton to={repayPath} icon={<RefreshCw size={15} aria-hidden="true" />}>Try payment again</LinkButton> : null}
          {state !== "success" ? <LinkButton to={orderPath} variant="ghost">Check orders</LinkButton> : null}
          <LinkButton to="/products" variant="secondary" icon={<ShoppingBag size={15} aria-hidden="true" />}>Browse catalog</LinkButton>
        </div>
      )}
      className={`info-page payment-return-page payment-return-${state}`}
    >
      {body}
    </EmptyState>
  );
}
